import { useMemo } from 'react';
import {
  X, ChartBar, FileXls, FilePdf, Printer,
  CheckCircle, Warning, Clock, ListChecks, Flag,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  pendente: { label: 'Pendente', color: '#B45309', bg: '#FEF3C7' },
  atrasada: { label: 'Atrasada', color: '#B91C1C', bg: '#FEE2E2' },
  concluida: { label: 'Concluida', color: '#1B4332', bg: '#DCFCE7' },
};
const PRI_LABEL = { baixa: 'Baixa', normal: 'Normal', urgente: 'Urgente' };
const CAT_LABEL = {
  financeiro: 'Financeiro', cliente: 'Cliente', equipe: 'Equipe',
  fornecedor: 'Fornecedor', operacao: 'Operacao', pessoal: 'Pessoal',
};

const fmtBR = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : iso;
};

const fmtConcluidaEm = (iso) => {
  if (!iso) return '';
  const s = iso.substring(0, 10);
  return fmtBR(s);
};

export const RelatorioTarefasDrawer = ({
  dataInicio, dataFim, filterStatus, filterPrioridade, search,
  tarefas, onClose,
}) => {
  // `tarefas` ja vem com todos os filtros da tela aplicados (client-side).
  // Cards do relatorio refletem o periodo selecionado (mesma logica do gestor).
  const resumo = useMemo(() => {
    const total = tarefas.length;
    const pendentes = tarefas.filter(t => t.status === 'pendente').length;
    const atrasadas = tarefas.filter(t => t.status === 'atrasada').length;
    const concluidas = tarefas.filter(t => t.status === 'concluida').length;
    const urgentes = tarefas.filter(t => t.prioridade === 'urgente' && t.status !== 'concluida').length;
    const pct = total ? Math.round((concluidas / total) * 1000) / 10 : 0;
    return { total, pendentes, atrasadas, urgentes, concluidas, pct };
  }, [tarefas]);

  const buildExportUrl = (kind) => {
    const params = new URLSearchParams();
    if (dataInicio) params.set('data_inicio', dataInicio);
    if (dataFim) params.set('data_fim', dataFim);
    if (filterStatus && filterStatus !== 'todos') params.set('status', filterStatus);
    if (filterPrioridade && filterPrioridade !== 'todas') params.set('prioridade', filterPrioridade);
    if (search) params.set('busca', search);
    return `${API}/tarefas/relatorio/export/${kind}?${params.toString()}`;
  };

  const handlePrint = () => window.print();

  const periodoLabel = (() => {
    if (dataInicio && dataFim) return `${fmtBR(dataInicio)} a ${fmtBR(dataFim)}`;
    if (dataInicio) return `a partir de ${fmtBR(dataInicio)}`;
    if (dataFim) return `ate ${fmtBR(dataFim)}`;
    return 'todos os registros';
  })();

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="relatorio-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm print:hidden" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-5xl bg-[#F7F6F4] shadow-2xl overflow-y-auto print:max-w-full print:shadow-none">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#E4E4E7] px-6 py-4 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <ChartBar size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Relatorio de Tarefas</h2>
              <p className="text-xs text-[#71717A]">Consulte a operacao realizada por periodo.</p>
            </div>
          </div>
          <button data-testid="btn-close-relatorio" onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Metadados */}
          <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <div><span className="text-[#71717A]">Periodo:</span> <b className="text-[#18181B]" data-testid="rel-periodo">{periodoLabel}</b></div>
            {(filterStatus && filterStatus !== 'todos') && (
              <div><span className="text-[#71717A]">Status:</span> <b className="text-[#18181B]">{STATUS_CFG[filterStatus]?.label || filterStatus}</b></div>
            )}
            {(filterPrioridade && filterPrioridade !== 'todas') && (
              <div><span className="text-[#71717A]">Prioridade:</span> <b className="text-[#18181B]">{PRI_LABEL[filterPrioridade] || filterPrioridade}</b></div>
            )}
            {search && (
              <div><span className="text-[#71717A]">Busca:</span> <b className="text-[#18181B]">{search}</b></div>
            )}
          </div>

          {/* Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="rel-resumo">
            <ResumoCard label="Total" value={resumo.total} color="#71717A" Ic={ListChecks} testid="rel-total" />
            <ResumoCard label="Pendentes" value={resumo.pendentes} color="#B45309" Ic={Clock} testid="rel-pendentes" />
            <ResumoCard label="Atrasadas" value={resumo.atrasadas} color="#B91C1C" Ic={Warning} testid="rel-atrasadas" />
            <ResumoCard label="Urgentes" value={resumo.urgentes} color="#B91C1C" Ic={Flag} testid="rel-urgentes" />
            <ResumoCard label="Concluidas" value={resumo.concluidas} color="#1B4332" Ic={CheckCircle} testid="rel-concluidas" />
            <ResumoCard label="% Conclusao" value={`${resumo.pct}%`} color="#1B4332" Ic={ChartBar} testid="rel-pct" />
          </div>

          {/* Acoes */}
          <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
            <p className="text-xs text-[#71717A]">
              <b>{resumo.total}</b> tarefa(s) exibida(s). O relatorio respeita os filtros ativos da tela.
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={async () => {
                  const { downloadWithAuth } = await import('@/lib/apiClient');
                  await downloadWithAuth(buildExportUrl('excel'), 'relatorio_tarefas.xlsx');
                }}
                data-testid="btn-rel-export-excel"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B]"
              >
                <FileXls size={16} weight="bold" /> Excel
              </button>
              <button type="button" onClick={async () => {
                  const { downloadWithAuth } = await import('@/lib/apiClient');
                  await downloadWithAuth(buildExportUrl('pdf'), 'relatorio_tarefas.pdf');
                }}
                data-testid="btn-rel-export-pdf"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B]"
              >
                <FilePdf size={16} weight="bold" /> PDF
              </button>
              <button onClick={handlePrint}
                data-testid="btn-rel-imprimir"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B]"
              >
                <Printer size={16} weight="bold" /> Imprimir
              </button>
            </div>
          </div>

          {/* Detalhamento */}
          <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E4E4E7]">
              <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Detalhamento das tarefas</p>
            </div>
            {tarefas.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#71717A]" data-testid="rel-empty">
                Nenhuma tarefa no periodo com os filtros aplicados.
              </div>
            ) : (
              <div className="divide-y divide-[#F4F4F5]" data-testid="rel-lista">
                {tarefas.map(t => <TarefaRow key={t.id} t={t} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResumoCard = ({ label, value, color, Ic, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E4E4E7] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Ic size={14} weight="bold" style={{ color }} />
      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color }}>{label}</p>
    </div>
    <p className="text-2xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{value}</p>
  </div>
);

const TarefaRow = ({ t }) => {
  const stCfg = STATUS_CFG[t.status] || { label: t.status, color: '#71717A', bg: '#F4F4F5' };
  return (
    <div className="px-4 py-3 flex flex-wrap items-center gap-3 text-xs" data-testid={`rel-row-${t.id}`}>
      <div className="w-20 text-[#71717A] flex-shrink-0">
        <div>{fmtBR(t.data)}</div>
        {t.horario && <div className="text-[10px] text-[#A1A1AA]">{t.horario}</div>}
      </div>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{t.titulo}</p>
        {t.descricao && <p className="text-[10px] text-[#71717A] mt-0.5 line-clamp-2">{t.descricao}</p>}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Chip label={PRI_LABEL[t.prioridade] || t.prioridade} color={t.prioridade === 'urgente' ? '#B91C1C' : (t.prioridade === 'baixa' ? '#71717A' : '#1B4332')} />
        <Chip label={CAT_LABEL[t.categoria] || t.categoria} color="#71717A" />
        {t.responsavel && <Chip label={t.responsavel} color="#0369A1" />}
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold"
          style={{ backgroundColor: stCfg.bg, color: stCfg.color }}>{stCfg.label}</span>
        {t.concluida_em && (
          <span className="text-[10px] text-[#A1A1AA]">Conc.: {fmtConcluidaEm(t.concluida_em)}</span>
        )}
      </div>
    </div>
  );
};

const Chip = ({ label, color }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-[#F7F6F4] border border-[#E4E4E7]"
    style={{ color }}>{label}</span>
);
